import { sanitizeHtml } from '../../lib/sanitizeHtml';
import React, { useState, useEffect } from 'react';
import {
  PenTool,
  Plus,
  Edit,
  Trash2,
  Eye,
  Loader,
  CheckCircle,
  XCircle,
  User,
  Users,
  Mail,
  AlertCircle
} from 'lucide-react';
import EmailSignatureEditor from './EmailSignatureEditor';
import { useAuth } from '../Auth/AuthContext';
import { useTranslation } from 'react-i18next';

const EmailSignatureManager = ({ backendUrl, authToken, emailAccounts }) => {
  const { t } = useTranslation('admin');
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [signatures, setSignatures] = useState([]);
  const [showEditor, setShowEditor] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [editingSignature, setEditingSignature] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [previewSignature, setPreviewSignature] = useState(null);

  // Vérifier si l'utilisateur peut gérer les signatures collectives
  const canManageSharedSignatures = user?.admin_permissions?.can_manage_shared_signatures || false;

  useEffect(() => {
    loadSignatures();
  }, [emailAccounts]);

  const loadSignatures = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${backendUrl}/api/admin/mail/signatures`, {
        headers: { 'Authorization': `Bearer ${authToken}` }
      });

      if (response.ok) {
        const data = await response.json();
        setSignatures(data);
      }
    } catch (error) {
      console.error('Erreur chargement signatures:', error);
    } finally {
      setLoading(false);
    }
  };

  const showSuccess = (msg) => {
    setSuccessMessage(msg);
    setTimeout(() => setSuccessMessage(''), 4000);
  };

  const showError = (msg) => {
    setErrorMessage(msg);
    setTimeout(() => setErrorMessage(''), 5000);
  };

  const handleCreateSignature = (account) => {
    setSelectedAccount(account);
    setEditingSignature(null);
    setShowEditor(true);
  };

  const handleEditSignature = (account, signature) => {
    setSelectedAccount(account);
    setEditingSignature(signature);
    setShowEditor(true);
  };

  const handleDeleteSignature = async (signatureId) => {
    if (!window.confirm(t('emailSignature.confirmDelete'))) {
      return;
    }

    try {
      const response = await fetch(`${backendUrl}/api/admin/mail/signatures/${signatureId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${authToken}` }
      });

      if (response.ok) {
        showSuccess(t('emailSignature.deleted'));
        loadSignatures();
      } else {
        const error = await response.json();
        showError(error.detail || t('emailSignature.deleteError'));
      }
    } catch (error) {
      showError(t('emailSignature.connError'));
    }
  };

  const handleSaveSignature = () => {
    setShowEditor(false);
    loadSignatures();
    showSuccess(t('emailSignature.saved'));
  };

  const getSignatureForAccount = (accountId) => {
    return signatures.find(s => s.email_account_id === accountId);
  };

  const canEditSignature = (account) => {
    // Super admin peut tout gérer
    if (user?.role === 'super_admin') {
      return true;
    }
    
    if (account.account_type === 'individual') {
      // L'admin peut gérer ses propres comptes individuels
      // OU si can_manage_shared_signatures est activé (pour les admins avec droits étendus)
      return account.assigned_admin_id === user?.id || canManageSharedSignatures;
    }
    
    // Pour les comptes partagés, il faut le droit can_manage_shared_signatures
    return canManageSharedSignatures;
  };

  // Séparer les comptes individuels et partagés
  const individualAccounts = emailAccounts.filter(acc => acc.account_type === 'individual');
  const sharedAccounts = emailAccounts.filter(acc => acc.account_type === 'shared');

  if (showEditor && selectedAccount) {
    return (
      <EmailSignatureEditor
        backendUrl={backendUrl}
        authToken={authToken}
        emailAccount={selectedAccount}
        existingSignature={editingSignature}
        onSave={handleSaveSignature}
        onCancel={() => setShowEditor(false)}
      />
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-bold text-slate-900 flex items-center">
            <PenTool className="mr-2 text-indigo-600" size={24} />
            {t('emailSignature.title')}
          </h3>
          <p className="text-sm text-slate-600 mt-1">
            {t('emailSignature.subtitle')}
          </p>
        </div>
      </div>

      {/* Messages */}
      {successMessage && (
        <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 flex items-center">
          <CheckCircle className="text-emerald-600 mr-3" size={20} />
          <span className="text-emerald-700">{successMessage}</span>
        </div>
      )}
      {errorMessage && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-center">
          <XCircle className="text-red-600 mr-3" size={20} />
          <span className="text-red-700">{errorMessage}</span>
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader className="animate-spin text-indigo-600" size={32} />
        </div>
      ) : (
        <>
          {/* Section Signatures Individuelles */}
          <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
            <div className="p-4 bg-gradient-to-r from-blue-50 to-indigo-50 border-b border-slate-200">
              <h4 className="font-semibold text-slate-900 flex items-center">
                <User className="mr-2 text-blue-600" size={18} />
                {t('emailSignature.individualTitle')}
              </h4>
            </div>
            
            {individualAccounts.length === 0 ? (
              <div className="p-6 text-center text-slate-500">
                <Mail className="mx-auto mb-2 text-slate-400" size={32} />
                <p>{t('emailSignature.noIndividual')}</p>
              </div>
            ) : (
              <div className="divide-y divide-slate-100">
                {individualAccounts.map(account => {
                  const signature = getSignatureForAccount(account.id);
                  const canEdit = canEditSignature(account);
                  
                  return (
                    <div key={account.id} className="p-4 hover:bg-slate-50 transition-colors">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                          <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                            <Mail className="text-blue-600" size={18} />
                          </div>
                          <div>
                            <p className="font-medium text-slate-900">{account.email_address}</p>
                            <p className="text-sm text-slate-500">{account.display_name}</p>
                          </div>
                        </div>
                        
                        <div className="flex items-center space-x-2">
                          {signature ? (
                            <>
                              <span className="px-2 py-1 bg-emerald-100 text-emerald-700 text-xs rounded-full">
                                {t('emailSignature.configured')}
                              </span>
                              {canEdit && (
                                <>
                                  <button
                                    onClick={() => setPreviewSignature(signature)}
                                    className="p-2 text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors"
                                    title={t('emailSignature.previewAction')}
                                  >
                                    <Eye size={16} />
                                  </button>
                                  <button
                                    onClick={() => handleEditSignature(account, signature)}
                                    className="p-2 text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors"
                                    title={t('emailSignature.edit')}
                                  >
                                    <Edit size={16} />
                                  </button>
                                  <button
                                    onClick={() => handleDeleteSignature(signature.id)}
                                    className="p-2 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                    title={t('emailSignature.delete')}
                                  >
                                    <Trash2 size={16} />
                                  </button>
                                </>
                              )}
                            </>
                          ) : (
                            <>
                              <span className="px-2 py-1 bg-slate-100 text-slate-500 text-xs rounded-full">
                                {t('emailSignature.none')}
                              </span>
                              {canEdit && (
                                <button
                                  onClick={() => handleCreateSignature(account)}
                                  className="p-2 text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors flex items-center space-x-1"
                                  title={t('emailSignature.createTitle')}
                                >
                                  <Plus size={16} />
                                  <span className="text-sm">{t('emailSignature.create')}</span>
                                </button>
                              )}
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Section Signatures Collectives */}
          <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
            <div className="p-4 bg-gradient-to-r from-purple-50 to-pink-50 border-b border-slate-200">
              <div className="flex items-center justify-between">
                <h4 className="font-semibold text-slate-900 flex items-center">
                  <Users className="mr-2 text-purple-600" size={18} />
                  {t('emailSignature.sharedTitle')}
                </h4>
                {!canManageSharedSignatures && (
                  <span className="text-xs text-amber-600 bg-amber-50 px-2 py-1 rounded-full flex items-center">
                    <AlertCircle size={12} className="mr-1" />
                    {t('emailSignature.readOnly')}
                  </span>
                )}
              </div>
            </div>
            
            {sharedAccounts.length === 0 ? (
              <div className="p-6 text-center text-slate-500">
                <Users className="mx-auto mb-2 text-slate-400" size={32} />
                <p>{t('emailSignature.noShared')}</p>
              </div>
            ) : (
              <div className="divide-y divide-slate-100">
                {sharedAccounts.map(account => {
                  const signature = getSignatureForAccount(account.id);
                  const canEdit = canEditSignature(account);
                  
                  return (
                    <div key={account.id} className="p-4 hover:bg-slate-50 transition-colors">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                          <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center">
                            <Users className="text-purple-600" size={18} />
                          </div>
                          <div>
                            <p className="font-medium text-slate-900">{account.email_address}</p>
                            <p className="text-sm text-slate-500">{account.display_name}</p>
                          </div>
                        </div>
                        
                        <div className="flex items-center space-x-2">
                          {signature ? (
                            <>
                              <span className="px-2 py-1 bg-emerald-100 text-emerald-700 text-xs rounded-full">
                                {t('emailSignature.configured')}
                              </span>
                              <button
                                onClick={() => setPreviewSignature(signature)}
                                className="p-2 text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors"
                                title={t('emailSignature.previewAction')}
                              >
                                <Eye size={16} />
                              </button>
                              {canEdit && (
                                <>
                                  <button
                                    onClick={() => handleEditSignature(account, signature)}
                                    className="p-2 text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors"
                                    title={t('emailSignature.edit')}
                                  >
                                    <Edit size={16} />
                                  </button>
                                  <button
                                    onClick={() => handleDeleteSignature(signature.id)}
                                    className="p-2 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                    title={t('emailSignature.delete')}
                                  >
                                    <Trash2 size={16} />
                                  </button>
                                </>
                              )}
                            </>
                          ) : (
                            <>
                              <span className="px-2 py-1 bg-slate-100 text-slate-500 text-xs rounded-full">
                                {t('emailSignature.none')}
                              </span>
                              {canEdit && (
                                <button
                                  onClick={() => handleCreateSignature(account)}
                                  className="p-2 text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors flex items-center space-x-1"
                                  title={t('emailSignature.createTitle')}
                                >
                                  <Plus size={16} />
                                  <span className="text-sm">{t('emailSignature.create')}</span>
                                </button>
                              )}
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </>
      )}

      {/* Modal de prévisualisation */}
      {previewSignature && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[80vh] overflow-y-auto">
            <div className="p-6 border-b border-slate-200 flex items-center justify-between">
              <h3 className="text-lg font-bold text-slate-900">{t('emailSignature.previewTitle')}</h3>
              <button
                onClick={() => setPreviewSignature(null)}
                className="p-2 text-slate-400 hover:text-slate-600 rounded-lg"
              >
                <XCircle size={20} />
              </button>
            </div>
            <div className="p-6">
              <div className="border border-slate-200 rounded-xl p-4 bg-slate-50">
                {/* Contenu */}
                {previewSignature.content_html && (
                  <div 
                    className="mb-4"
                    dangerouslySetInnerHTML={{ __html: sanitizeHtml(previewSignature.content_html) }}
                  />
                )}
                
                {/* Logo */}
                {previewSignature.logo_enabled && previewSignature.logo_url && (
                  <div className="mb-4">
                    <img 
                      src={previewSignature.logo_url} 
                      alt="Logo" 
                      className="max-h-16 max-w-48"
                    />
                  </div>
                )}
                
                {/* Réseaux sociaux */}
                {previewSignature.social_links?.length > 0 && (
                  <div className="mb-4 flex items-center space-x-3">
                    {previewSignature.social_links.filter(s => s.enabled).map(social => (
                      <a 
                        key={social.platform}
                        href={social.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-indigo-600 hover:text-indigo-800 text-sm"
                      >
                        {social.platform}
                      </a>
                    ))}
                  </div>
                )}
                
                {/* Site web */}
                {previewSignature.website_enabled && previewSignature.website_url && (
                  <div className="mb-4">
                    <a 
                      href={previewSignature.website_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-indigo-600 hover:text-indigo-800 text-sm"
                    >
                      🌐 {previewSignature.website_url}
                    </a>
                  </div>
                )}
                
                {/* Disclaimer */}
                {previewSignature.disclaimer_enabled && previewSignature.disclaimer_text && (
                  <div className="mt-4 pt-4 border-t border-slate-300 text-xs text-slate-500">
                    {previewSignature.disclaimer_text}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmailSignatureManager;
